import React from 'react';
import {
  Box,
  Container,
  Grid,
  Card,
  CardContent,
  useTheme,
  Avatar,
  Typography,
} from '@mui/material';
import {
  TrendingUp,
  ArrowUpward,
  ArrowDownward,
} from '@mui/icons-material';
import { SvgIconComponent } from '@mui/icons-material';
import ShapeBackground from '../common/ShapeBackground';
import RecentTransactions from './RecentTransactions';
import { useAnalyticsSummary } from '../../hooks/useAnalyticsSummary';
import BalanceOverviewChart from './BalanceOverviewChart';
import TotalBalanceCard from './TotalBalanceCard';
import MonthlyIncomeCard from './MonthlyIncomeCard';
import MonthlyExpensesCard from './MonthlyExpensesCard';
import SpendingDistributionCard from './SpendingDistributionCard';
import UpcomingDisbursementsCard from './UpcomingDisbursementsCard';

interface StatCardProps {
  title: string;
  value: string;
  icon: SvgIconComponent;
  color: 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success' | 'tertiary';
  subtitle: string;
  trend?: number;
}

const DashboardPage: React.FC = () => {
  const theme = useTheme();
  const { data: analytics, isLoading, isError } = useAnalyticsSummary();

  console.log(analytics);

  const StatCard: React.FC<StatCardProps> = ({ title, value, icon: Icon, color, subtitle, trend = 0 }) => (
    <Card sx={{ 
      height: '100%',
      backdropFilter: 'blur(20px)',
      backgroundColor: 'rgba(255, 255, 255, 0.9)',
      boxShadow: '0px 2px 24px rgba(0, 0, 0, 0.1)',
      borderRadius: 3,
      transition: 'transform 0.2s ease-in-out',
      '&:hover': {
        transform: 'translateY(-4px)',
      },
    }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Avatar 
            sx={{ 
              bgcolor: `${color}.light`,
              color: `${color}.main`,
              mr: 2,
              boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.1)',
            }}
          >
            <Icon />
          </Avatar>
          <Typography variant="h6" sx={{ color: theme.palette.text.primary, fontWeight: 500 }}>
            {title}
          </Typography>
        </Box>
        <Typography variant="h4" sx={{ mb: 1, fontWeight: 600, color: theme.palette.text.primary }}>
          {value}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          {trend !== 0 && (
            <Typography
              variant="body2"
              sx={{
                color: trend > 0 ? theme.palette.success.main : theme.palette.error.main,
                display: 'flex',
                alignItems: 'center',
                mr: 1,
                fontWeight: 500,
              }}
            >
              {trend > 0 ? <ArrowUpward fontSize="small" /> : <ArrowDownward fontSize="small" />}
              {Math.abs(trend)}%
            </Typography>
          )}
          <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
            {subtitle}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        position: 'relative',
        pt: 8,
      }}
    >
      <ShapeBackground />
      
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        <Grid container spacing={3}>
          {/* Upcoming Disbursements - full width, prominent */}
          <Grid item xs={12}>
            <UpcomingDisbursementsCard />
          </Grid>

          {/* Stats Cards */}
          <Grid item xs={12} sm={6} md={3}>
            <TotalBalanceCard />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <MonthlyIncomeCard />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <MonthlyExpensesCard />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Savings Rate"
              value={isLoading ? 'Loading...' : isError ? 'Error' : `${(analytics?.savingsRate ?? 0 * 100).toFixed(2)}%`}
              icon={TrendingUp}
              color="tertiary"
              subtitle="Last 30 days"
              trend={0}
            />
          </Grid>

          {/* Main Chart */}
          <Grid item xs={12} md={8}>
            <BalanceOverviewChart />
          </Grid>

          {/* Spending Distribution */}
          <Grid item xs={12} md={4}>
            <SpendingDistributionCard />
          </Grid>

          {/* Recent Transactions */}
          <Grid item xs={12}>
            <RecentTransactions />
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default DashboardPage; 